import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateProjectData, UpdateProjectData, Project } from '../../types';
import { clientService } from '../../services/clientService';
import { deletionRestoreService } from '../../services/deletionRestoreService';
import { useProjectWorkflowOptional } from '../../contexts/ProjectWorkflowContext';
import {
  notifyClientCreated,
  refetchWorkflowPanelQueries,
} from '../../utils/workflowPanelCache';
import {
  invalidateProjectAiSetupForUrlChange,
  websiteUrlsMatch,
} from '../../utils/aiSuggestionsEngine';
import { normalizeProjectWebsiteUrl } from '../../utils/competitorAnalysisUtils';
import ArchiveIconSvg from '../common/ArchiveIconSvg';
import { releaseModalScrollLock } from '../../utils/releaseModalScrollLock';

const MAX_COMPETITOR_LINKS = 10;

interface Client {
  id: string;
  name: string;
  email: string;
}

interface ProjectFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectData | UpdateProjectData) => Promise<void>;
  project?: Project;
  clients: Client[];
  loading?: boolean;
  isClientUser?: boolean;
  userClientId?: string;
  canCreateClient?: boolean;
  /** When set, client is fixed (hidden selector) — used on client detail page. */
  fixedClientId?: string;
  /** When editing, show Archive Project and call this on confirm. */
  onArchive?: (project: Project) => Promise<void>;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  open,
  onClose,
  onSubmit,
  project,
  clients,
  loading = false,
  isClientUser = false,
  userClientId = '',
  canCreateClient = false,
  fixedClientId = '',
  onArchive,
}) => {
  const queryClient = useQueryClient();
  const workflow = useProjectWorkflowOptional();
  const { data: archiveSettingsData } = useQuery({
    queryKey: ['deletion-restore-settings'],
    queryFn: () => deletionRestoreService.getSettings(),
    staleTime: 60_000,
    enabled: Boolean(onArchive),
  });
  const archiveRetentionLabel = deletionRestoreService.formatRetentionLabel(
    archiveSettingsData?.data?.retentionDays ?? 30,
  );
  const [formData, setFormData] = useState({
    projectName: project?.projectName || '',
    websiteUrl: project?.websiteUrl || '',
    companyName: project?.companyName || '',
    clientId: project?.clientId || '',
    budget: project?.budget?.toString() || '',
  });

  const [competitorLinks, setCompetitorLinks] = useState<string[]>(
    project?.competitor_links || ['']
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Inline "create new client" mode (admin / super admin / agency).
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [creatingClientBusy, setCreatingClientBusy] = useState(false);
  // Clients created inline this session, merged into the dropdown so the newly
  // saved client can be auto-selected (and changed) without a full refresh.
  const [extraClients, setExtraClients] = useState<Client[]>([]);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // Keep local form state in sync when dialog opens or selected project changes
  useEffect(() => {
    setFormData({
      projectName: project?.projectName || '',
      websiteUrl: project?.websiteUrl || '',
      companyName: project?.companyName || '',
      clientId: project?.clientId || fixedClientId || (isClientUser ? userClientId : ''),
      budget: project?.budget?.toString() || '',
    });
    setCompetitorLinks(project?.competitor_links || ['']);
    setErrors({});
    setCreatingClient(false);
    setNewClientName('');
    setNewClientEmail('');
    setArchiveConfirmOpen(false);
    setArchiving(false);
    setArchiveError(null);
  }, [project, open, isClientUser, userClientId, fixedClientId]);

  useEffect(() => {
    if (open || archiveConfirmOpen) return;
    releaseModalScrollLock();
  }, [open, archiveConfirmOpen]);

  const handleArchiveConfirm = async () => {
    if (!project || !onArchive) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await onArchive(project);
      setArchiveConfirmOpen(false);
      onClose();
      releaseModalScrollLock();
    } catch (error: any) {
      setArchiveError(
        error?.response?.data?.error ||
          error?.message ||
          'Failed to archive project. Please try again.'
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCompetitorLinkChange = (index: number, value: string) => {
    const newLinks = [...competitorLinks];
    newLinks[index] = value;
    setCompetitorLinks(newLinks);
  };

  const addCompetitorLink = () => {
    if (competitorLinks.length >= MAX_COMPETITOR_LINKS) return;
    setCompetitorLinks([...competitorLinks, '']);
  };

  const removeCompetitorLink = (index: number) => {
    if (competitorLinks.length > 1) {
      const newLinks = competitorLinks.filter((_, i) => i !== index);
      setCompetitorLinks(newLinks);
    }
  };

  // Save an inline-created client, then auto-select it for the project.
  const handleSaveNewClient = async () => {
    const newErrors: Record<string, string> = {};
    if (!newClientName.trim()) {
      newErrors.newClientName = 'Client name is required';
    }
    if (!newClientEmail.trim()) {
      newErrors.newClientEmail = 'Client email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newClientEmail.trim())) {
      newErrors.newClientEmail = 'Please enter a valid email address';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors((p) => ({ ...p, ...newErrors }));
      return;
    }

    setCreatingClientBusy(true);
    try {
      const saved = await clientService.createClient({
        name: newClientName.trim(),
        email: newClientEmail.trim(),
      });
      // Merge into the dropdown options and auto-select the new client.
      setExtraClients((prev) => [{ id: saved.id, name: saved.name, email: saved.email }, ...prev]);
      setFormData((prev) => ({ ...prev, clientId: saved.id }));
      setCreatingClient(false);
      setNewClientName('');
      setNewClientEmail('');
      setErrors((p) => ({ ...p, clientId: '', newClientName: '', newClientEmail: '' }));
      // Refresh any cached client lists so the new client shows up immediately
      // (sidebar panel, project-form dropdown, clients page, etc.).
      notifyClientCreated(queryClient, saved, workflow?.focusClientInPanel);
      await refetchWorkflowPanelQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
    } catch (err: any) {
      if (err.response?.data?.field === 'email' || err.response?.data?.error?.includes('email')) {
        const apiMessage = err.response?.data?.error as string | undefined;
        setErrors((p) => ({
          ...p,
          newClientEmail:
            apiMessage ||
            'A client with this email already exists. Pick the existing client instead.',
        }));
      } else {
        setErrors((p) => ({ ...p, newClientName: err.response?.data?.error || 'Failed to create client' }));
      }
    } finally {
      setCreatingClientBusy(false);
    }
  };

  // Existing clients plus any created inline this session (newest first).
  const allClients: Client[] = [...extraClients, ...clients.filter((c) => !extraClients.some((e) => e.id === c.id))];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project name is required';
    }

    // Skip clientId validation for CLIENT users - it will be set automatically.
    // A new client must be saved (which selects it) before the project can be created.
    if (!isClientUser && !formData.clientId) {
      newErrors.clientId = creatingClient
        ? 'Please save the new client first'
        : 'Client is required';
    }

    // Validate / normalize website URL (domain or full URL with http/https)
    if (formData.websiteUrl && formData.websiteUrl.trim()) {
      const normalized = normalizeProjectWebsiteUrl(formData.websiteUrl);
      if (normalized === null) {
        newErrors.websiteUrl =
          'Enter a valid website or domain (e.g. example.com or https://example.com)';
      }
    }

    const filledCompetitorLinks = competitorLinks.filter((link) => link.trim() !== '');
    if (filledCompetitorLinks.length > MAX_COMPETITOR_LINKS) {
      newErrors.competitor_links = `You can add up to ${MAX_COMPETITOR_LINKS} competitor URLs`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Filter out empty competitor links
      const filteredCompetitorLinks = competitorLinks.filter(link => link.trim() !== '');
      const normalizedWebsiteUrl = formData.websiteUrl.trim()
        ? normalizeProjectWebsiteUrl(formData.websiteUrl)
        : '';
      if (formData.websiteUrl.trim() && normalizedWebsiteUrl === null) {
        setErrors((prev) => ({
          ...prev,
          websiteUrl:
            'Enter a valid website or domain (e.g. example.com or https://example.com)',
        }));
        return;
      }
      if (normalizedWebsiteUrl && normalizedWebsiteUrl !== formData.websiteUrl) {
        setFormData((prev) => ({ ...prev, websiteUrl: normalizedWebsiteUrl }));
      }
      
      let payload: CreateProjectData | UpdateProjectData = {
        projectName: formData.projectName,
        websiteUrl: normalizedWebsiteUrl || undefined,
        companyName: formData.companyName || undefined,
        clientId: formData.clientId,
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        competitor_links: filteredCompetitorLinks.length > 0 ? filteredCompetitorLinks : undefined,
      };

      // For CLIENT users, ensure clientId is set
      if (isClientUser) {
        if (userClientId) {
          payload.clientId = userClientId;
        } else {
          setErrors({ clientId: 'Your account is not linked to a client. Please contact administrator.' });
          return;
        }
      }

      // Validate form
      if (!validateForm()) {
        return;
      }

      const previousUrl = project?.websiteUrl || '';
      const nextUrl = normalizedWebsiteUrl || '';
      await onSubmit(payload);

      // Changing the project website invalidates prior AI analysis / picks.
      if (project?.id && !websiteUrlsMatch(previousUrl, nextUrl)) {
        invalidateProjectAiSetupForUrlChange(project.id);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const newErrors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => {
          newErrors[err.path || err.param] = err.msg;
        });
        setErrors(newErrors);
      } else if (error.response?.data?.error) {
        const msg = String(error.response.data.error);
        if (/website url|already used/i.test(msg)) {
          setErrors((prev) => ({ ...prev, websiteUrl: msg }));
        } else {
          setErrors((prev) => ({ ...prev, projectName: msg }));
        }
      }
    }
  };

  
  return (
    <>
    <Dialog 
      open={open} 
      onClose={loading || creatingClientBusy ? () => {} : onClose}
      maxWidth="md" 
      fullWidth
      scroll="paper"
      disableScrollLock
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
          border: '1px solid var(--border)',
          maxWidth: '560px',
          width: '100%',
          maxHeight: 'calc(100vh - 48px)',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '22px 26px 18px', borderBottom: '1px solid var(--border-light)' }}>
        <Box>
          <Typography sx={{ fontSize: '16px !important', fontWeight: 600, letterSpacing: '-0.3px', color: '#4f6ef7' }}>
            {project ? 'Edit Project' : 'Add New Project'}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'var(--text-muted)', mt: 0.5 }}>
            {project ? 'Update the details for this project' : 'Create a new link-building project for a client'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            width: '30px',
            height: '30px',
            borderRadius: '6px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s',
            flexShrink: 0,
            mt: 0.5,
            '&:hover': {
              background: 'var(--bg)'
            }
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </IconButton>
      </Box>
      <Box sx={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            Project Name <span style={{ color: 'var(--red)', marginLeft: '2px' }}>*</span>
          </Typography>
          <input
            type="text"
            placeholder="e.g. Aghnami Law Group — SEO Campaign"
            value={formData.projectName}
            onChange={(e) => handleChange('projectName', e.target.value)}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '9px 12px',
              fontFamily: 'inherit',
              fontSize: '14px',
              color: 'var(--text-primary)',
              background: 'var(--surface)',
              outline: 'none',
              transition: 'all 0.15s',
              borderColor: errors.projectName ? 'var(--red)' : 'var(--border)'
            }}
            onFocus={(e) => {
              if (!errors.projectName) {
                e.target.style.borderColor = 'var(--accent)';
                e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.08)';
              }
            }}
            onBlur={(e) => {
              if (!errors.projectName) {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.boxShadow = 'none';
              }
            }}
          />
          {errors.projectName && (
            <Typography sx={{ fontSize: '11.5px', color: 'var(--red)' }}>
              {errors.projectName}
            </Typography>
          )}
        </Box>

        {fixedClientId && !isClientUser && (
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
            }}
          >
            <Typography sx={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Client
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', mt: 0.25 }}>
              {allClients.find((c) => c.id === fixedClientId)?.name || 'Selected client'}
            </Typography>
          </Box>
        )}

        {!isClientUser && !fixedClientId && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Client <span style={{ color: 'var(--red)', marginLeft: '2px' }}>*</span>
              </Typography>
              {canCreateClient && (
                <Typography
                  component="button"
                  type="button"
                  onClick={() => {
                    setCreatingClient((v) => !v);
                    setErrors((prev) => ({ ...prev, clientId: '', newClientName: '', newClientEmail: '' }));
                  }}
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--accent)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {creatingClient ? 'Select existing client' : '+ Add new client'}
                </Typography>
              )}
            </Box>

            {!creatingClient ? (
              <>
                <select
                  value={formData.clientId}
                  onChange={(e) => handleChange('clientId', e.target.value)}
                  style={{
                    appearance: 'none',
                    background: 'var(--surface)',
                    border: errors.clientId ? '1px solid var(--red)' : '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '9px 12px',
                    paddingRight: '32px',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239298a8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                  }}
                >
                  <option value="">Select a client…</option>
                  {allClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                {errors.clientId && (
                  <Typography sx={{ fontSize: '11.5px', color: 'var(--red)' }}>
                    {errors.clientId}
                  </Typography>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  padding: '14px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px dashed var(--border)',
                  background: 'var(--bg)',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                    Client Name <span style={{ color: 'var(--red)', marginLeft: '2px' }}>*</span>
                  </Typography>
                  <input
                    type="text"
                    placeholder="e.g. Aghnami Law Group"
                    value={newClientName}
                    onChange={(e) => {
                      setNewClientName(e.target.value);
                      if (errors.newClientName) setErrors((p) => ({ ...p, newClientName: '' }));
                    }}
                    style={{
                      border: errors.newClientName ? '1px solid var(--red)' : '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '9px 12px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      background: 'var(--surface)',
                      outline: 'none',
                    }}
                  />
                  {errors.newClientName && (
                    <Typography sx={{ fontSize: '11.5px', color: 'var(--red)' }}>
                      {errors.newClientName}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                    Client Email <span style={{ color: 'var(--red)', marginLeft: '2px' }}>*</span>
                  </Typography>
                  <input
                    type="email"
                    placeholder="e.g. contact@example.com"
                    value={newClientEmail}
                    onChange={(e) => {
                      setNewClientEmail(e.target.value);
                      if (errors.newClientEmail) setErrors((p) => ({ ...p, newClientEmail: '' }));
                    }}
                    style={{
                      border: errors.newClientEmail ? '1px solid var(--red)' : '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '9px 12px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      background: 'var(--surface)',
                      outline: 'none',
                    }}
                  />
                  {errors.newClientEmail && (
                    <Typography sx={{ fontSize: '11.5px', color: 'var(--red)' }}>
                      {errors.newClientEmail}
                    </Typography>
                  )}
                </Box>
                <Button
                  onClick={handleSaveNewClient}
                  disabled={creatingClientBusy}
                  sx={{
                    alignSelf: 'flex-start',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '7px 16px',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    background: 'var(--accent)',
                    color: 'white',
                    '&:hover': { background: 'var(--accent-hover)' },
                    '&:disabled': { opacity: 0.6, cursor: 'not-allowed', color: 'white' },
                  }}
                >
                  {creatingClientBusy ? 'Saving…' : 'Save client'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            Website URL
          </Typography>
          <input
            type="text"
            inputMode="url"
            autoComplete="url"
            placeholder="example.com or https://example.com"
            value={formData.websiteUrl}
            onChange={(e) => handleChange('websiteUrl', e.target.value)}
            style={{
              border: errors.websiteUrl ? '1px solid var(--red)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '9px 12px',
              fontFamily: 'inherit',
              fontSize: '14px',
              color: 'var(--text-primary)',
              background: 'var(--surface)',
              outline: 'none',
              transition: 'all 0.15s'
            }}
            onFocus={(e) => {
              if (!errors.websiteUrl) {
                e.target.style.borderColor = 'var(--accent)';
                e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.08)';
              }
            }}
            onBlur={(e) => {
              if (!errors.websiteUrl) {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.boxShadow = 'none';
              }
              const raw = e.target.value.trim();
              if (!raw) return;
              const normalized = normalizeProjectWebsiteUrl(raw);
              if (normalized) {
                setFormData((prev) => ({ ...prev, websiteUrl: normalized }));
                setErrors((prev) => {
                  if (!prev.websiteUrl) return prev;
                  const next = { ...prev };
                  delete next.websiteUrl;
                  return next;
                });
              }
            }}
          />
          {errors.websiteUrl && (
            <Typography sx={{ fontSize: '11.5px', color: 'var(--red)' }}>
              {errors.websiteUrl}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Company Name
            </Typography>
            <input
              type="text"
              placeholder="e.g. Acme Law LLC"
              value={formData.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '9px 12px',
                fontFamily: 'inherit',
                fontSize: '14px',
                color: 'var(--text-primary)',
                background: 'var(--surface)',
                outline: 'none',
                transition: 'all 0.15s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--accent)';
                e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
              Optional — company name for this project
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Project Budget ($)
            </Typography>
            <input
              type="number"
              placeholder="0.00"
              min="0"
              value={formData.budget}
              onChange={(e) => handleChange('budget', e.target.value)}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '9px 12px',
                fontFamily: 'inherit',
                fontSize: '14px',
                color: 'var(--text-primary)',
                background: 'var(--surface)',
                outline: 'none',
                transition: 'all 0.15s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--accent)';
                e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
              Optional — monthly budget allocated
            </Typography>
          </Box>
        </Box>

        {/* Competitor Links Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            Competitor Links
          </Typography>
          {competitorLinks.map((link, index) => (
            <Box key={index} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="url"
                placeholder="https://competitor.com"
                value={link}
                onChange={(e) => handleCompetitorLinkChange(index, e.target.value)}
                style={{
                  flex: 1,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '9px 12px',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  background: 'var(--surface)',
                  outline: 'none',
                  transition: 'all 0.15s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {competitorLinks.length > 1 && (
                <IconButton
                  onClick={() => removeCompetitorLink(index)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s',
                    '&:hover': {
                      background: 'var(--bg)',
                      borderColor: 'var(--red)'
                    }
                  }}
                  title="Remove competitor link"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </IconButton>
              )}
            </Box>
          ))}
          
          <Button
            onClick={addCompetitorLink}
            disabled={competitorLinks.length >= MAX_COMPETITOR_LINKS}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: 500,
              cursor: competitorLinks.length >= MAX_COMPETITOR_LINKS ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--surface)',
              color: competitorLinks.length >= MAX_COMPETITOR_LINKS ? 'var(--text-muted)' : 'var(--text-secondary)',
              border: '1px solid var(--border)',
              alignSelf: 'flex-start',
              opacity: competitorLinks.length >= MAX_COMPETITOR_LINKS ? 0.6 : 1,
              '&:hover': {
                background: competitorLinks.length >= MAX_COMPETITOR_LINKS ? 'var(--surface)' : 'var(--bg)',
                color: competitorLinks.length >= MAX_COMPETITOR_LINKS ? 'var(--text-muted)' : 'var(--text-primary)',
                borderColor: competitorLinks.length >= MAX_COMPETITOR_LINKS ? 'var(--border)' : 'var(--accent)',
              },
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add More
          </Button>
          
          <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
            Optional — add up to {MAX_COMPETITOR_LINKS} competitor websites for analysis
            {competitorLinks.length >= MAX_COMPETITOR_LINKS ? ' (maximum reached)' : ''}
          </Typography>
          {errors.competitor_links && (
            <Typography sx={{ fontSize: '11.5px', color: 'var(--red)' }}>
              {errors.competitor_links}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: project && onArchive ? 'space-between' : 'flex-end',
          gap: '10px',
          padding: '16px 26px',
          borderTop: '1px solid var(--border-light)',
        }}
      >
        {project && onArchive ? (
          <Button
            onClick={() => {
              setArchiveError(null);
              setArchiveConfirmOpen(true);
            }}
            disabled={loading || creatingClientBusy || archiving}
            startIcon={<ArchiveIconSvg size={14} color="#d97706" />}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              background: 'var(--amber-soft, #fffbeb)',
              backgroundColor: 'var(--amber-soft, #fffbeb)',
              color: 'var(--amber, #d97706)',
              border: '1px solid #fde68a',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: 'var(--amber, #d97706)',
                marginRight: '4px',
              },
              '&:hover': {
                background: '#fef3c7',
                backgroundColor: '#fef3c7',
                color: '#b45309',
                borderColor: '#fcd34d',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: '#b45309',
                },
              },
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed',
                color: 'var(--amber, #d97706)',
                backgroundColor: 'var(--amber-soft, #fffbeb)',
              },
            }}
          >
            Archive Project
          </Button>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Button
          onClick={onClose}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'inherit',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.15s',
            whiteSpace: 'nowrap',
            background: 'var(--surface)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            '&:hover': {
              background: 'var(--bg)',
              color: 'var(--text-primary)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || creatingClientBusy}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'inherit',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.15s',
            whiteSpace: 'nowrap',
            background: loading ? 'var(--text-muted)' : 'var(--accent)',
            color: 'white',
            boxShadow: loading ? 'none' : '0 1px 2px rgba(37,99,235,0.2)',
            '&:hover': {
              background: loading ? 'var(--text-muted)' : 'var(--accent-hover)',
              boxShadow: loading ? 'none' : '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)'
            },
            '&:disabled': {
              opacity: 0.6,
              cursor: 'not-allowed'
            }
          }}
        >
          {loading || creatingClientBusy ? (
            'Saving...'
          ) : project ? (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Update Project
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Create Project
            </>
          )}
        </Button>
        </Box>
      </Box>

    </Dialog>

      <Dialog
        open={archiveConfirmOpen}
        disableScrollLock
        onClose={() => {
          if (archiving) return;
          setArchiveConfirmOpen(false);
          setArchiveError(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid var(--border)',
            maxWidth: '560px',
            width: '100%',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '22px 26px 18px',
            borderBottom: '1px solid var(--border-light)',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.3px' }}>
              Archive Project
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Hidden from lists — restore from the <strong>Archive</strong> menu
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (archiving) return;
              setArchiveConfirmOpen(false);
              setArchiveError(null);
            }}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s',
              flexShrink: 0,
              mt: 0.5,
              '&:hover': { background: 'var(--bg)' },
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </Box>
        <Box sx={{ padding: '22px 26px' }}>
          <Typography sx={{ fontSize: '14px', color: 'var(--text-primary)', mb: 2 }}>
            Archive project <strong>"{project?.projectName}"</strong>?
            <br />
            It will be hidden from the sidebar and project lists. You can restore it within {archiveRetentionLabel} from{' '}
            <strong>Settings → Restore Archived Data</strong> (or Archive).
          </Typography>
          {archiveError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 'var(--radius-sm)', fontSize: '13px' }}>
              {archiveError}
            </Alert>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '16px 26px',
            borderTop: '1px solid var(--border-light)',
          }}
        >
          <Button
            onClick={() => {
              if (archiving) return;
              setArchiveConfirmOpen(false);
              setArchiveError(null);
            }}
            disabled={archiving}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              '&:hover': {
                background: 'var(--bg)',
                color: 'var(--text-primary)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleArchiveConfirm()}
            disabled={archiving}
            startIcon={<ArchiveIconSvg size={14} />}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              border: 'none',
              background: 'var(--amber)',
              color: 'white',
              '&:hover': { background: '#d97706' },
              '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
            }}
          >
            {archiving ? 'Archiving...' : 'Archive Project'}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default ProjectForm;
